import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Wallet as WalletIcon, ShieldCheck, Clock, CheckCircle2, XCircle, Loader2, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const STATUS = {
  pending: ["Under review", "#FFE300", Clock], approved: ["Approved — sending payment", "#00F0FF", Clock],
  paid: ["Paid", "#39FF14", CheckCircle2], rejected: ["Not approved — balance returned", "#FF003C", XCircle],
};

function CashoutForm({ w, onDone }) {
  const [method, setMethod] = useState("paypal");
  const [handle, setHandle] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rid] = useState(() => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
  const ph = { paypal: "you@email.com", venmo: "@username", cashapp: "$cashtag" }[method];
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/earnings/cashout", { method, handle, client_request_id: rid, confirm_fee: agree }); toast.success("Cash-out requested"); onDone(); }
    catch (er) { toast.error(formatApiError(er?.response?.data?.detail) || "Could not request cash-out"); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="glass rounded-2xl p-5 space-y-3 border border-[#39FF14]/30" data-testid="wallet-cashout-form">
      <div className="font-heading font-black text-lg">Request cash-out</div>
      <div className="grid grid-cols-3 gap-2">
        {[["paypal", "PayPal"], ["venmo", "Venmo"], ["cashapp", "Cash App"]].map(([k, l]) => (
          <button type="button" key={k} onClick={() => setMethod(k)} data-testid={`wallet-method-${k}`}
            className={`py-2 rounded-xl mono text-[11px] uppercase border ${method === k ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/10 text-white/60"}`}>{l}</button>
        ))}
      </div>
      <input required value={handle} onChange={(e) => setHandle(e.target.value)} placeholder={ph} data-testid="wallet-handle-input"
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#39FF14]/60" />
      <div className="rounded-xl bg-[#FFE300]/10 border border-[#FFE300]/30 p-3 text-xs text-white/80" data-testid="wallet-fee-disclosure">
        Your entire eligible balance will be withdrawn. A <b className="text-[#FFE300]">${w.fee_usd.toFixed(2)} cash-out fee</b> is deducted from the payout. Requests are reviewed by our team before payment is sent.
      </div>
      <label className="flex items-start gap-2 text-xs text-white/70 cursor-pointer">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="wallet-fee-confirm" className="mt-0.5 accent-[#39FF14]" />
        I understand the ${w.fee_usd.toFixed(2)} fee and confirm my payout details are correct.
      </label>
      <button disabled={!agree || busy} data-testid="wallet-cashout-submit"
        className="w-full py-3 rounded-full bg-[#39FF14] hover:bg-white text-black font-black mono text-xs uppercase tracking-widest disabled:opacity-40 flex items-center justify-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirm cash-out
      </button>
    </form>
  );
}

export default function Wallet() {
  const [w, setW] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/earnings/wallet").then((r) => setW(r.data)).catch(() => setW(null));
  useEffect(() => { load(); }, []);
  if (!w) return <div className="max-w-3xl mx-auto px-6 py-20 mono text-xs text-[#39FF14] animate-pulse">Loading wallet…</div>;
  const pct = w.progress_pct;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="wallet-page">
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14] flex items-center gap-2"><WalletIcon className="w-3 h-3" /> Jixxeo Wallet · ad-free</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter mt-2">Your earnings</h1>
      </div>

      <div className="glass rounded-3xl p-6 border border-[#39FF14]/30" data-testid="wallet-progress-card">
        <div className="flex items-end justify-between">
          <div className="mono text-[10px] uppercase tracking-widest text-white/50">Progress to ${w.min_cashout_usd.toFixed(0)} cash-out</div>
          <div className="font-heading font-black text-4xl text-[#39FF14]" data-testid="wallet-progress-pct">{pct}%</div>
        </div>
        <div className="mt-3 h-4 rounded-full bg-white/10 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full bg-gradient-to-r from-[#00F0FF] to-[#39FF14] transition-[width] duration-700" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button disabled={!w.can_cashout} onClick={() => setOpen((v) => !v)} data-testid="wallet-cashout-btn"
            className="bg-[#39FF14] hover:bg-white text-black font-black mono text-xs uppercase tracking-widest px-6 py-3 rounded-full disabled:bg-white/10 disabled:text-white/40 transition-colors">
            {w.has_pending_request ? "Cash-out in progress" : pct >= 100 ? "Cash out" : "Keep earning"}
          </button>
          {!w.account_age_ok && <span className="text-xs text-white/50">Cash-outs unlock once your account is {w.min_account_age_days} days old.</span>}
          {w.frozen && <span className="text-xs text-[#FF003C]">Cash-outs are paused on this account — contact support.</span>}
        </div>
      </div>
      {open && w.can_cashout && <CashoutForm w={w} onDone={() => { setOpen(false); load(); }} />}

      <section className="glass rounded-2xl p-5" data-testid="wallet-rules">
        <div className="font-heading font-black text-lg flex items-center gap-2"><Info className="w-4 h-4 text-[#00F0FF]" /> How earning & cash-outs work</div>
        <ul className="mt-3 space-y-1.5 text-sm text-white/70 list-disc pl-5">
          <li>Earn by: {w.eligible_activities.join(", ")}. No ads — ever.</li>
          <li>Play time counts only for apps you actually launch from JixxeoApps; daily earning limits apply.</li>
          <li>Minimum cash-out is <b className="text-white">${w.min_cashout_usd.toFixed(2)}</b>. Each cash-out has a flat <b className="text-[#FFE300]">${w.fee_usd.toFixed(2)}</b> fee.</li>
          <li>Accounts must be at least {w.min_account_age_days} days old, and only one cash-out can be in progress at a time.</li>
          <li>Every request is reviewed for fraud before payment via PayPal, Venmo or Cash App. Rejected requests return the balance to your wallet.</li>
          <li>Coins (from games, tips and stores) are separate and have no cash value.</li>
        </ul>
      </section>

      <section className="space-y-2" data-testid="wallet-requests">
        <h2 className="font-heading font-black text-lg">Cash-out requests</h2>
        {w.requests.length === 0 && <div className="text-sm text-white/40">No cash-outs yet.</div>}
        {w.requests.map((r) => {
          const [label, color, Icon] = STATUS[r.status] || [r.status, "#fff", Clock];
          return (
            <div key={r.id} className="glass rounded-xl px-4 py-3 flex items-center gap-3" data-testid={`wallet-request-${r.id}`}>
              <Icon className="w-4 h-4" style={{ color }} />
              <div className="flex-1 text-sm"><b style={{ color }}>{label}</b><div className="mono text-[10px] text-white/40">{r.method} · {r.handle_masked} · {new Date(r.created_at).toLocaleDateString()}</div></div>
            </div>
          );
        })}
      </section>

      <section className="space-y-2">
        <h2 className="font-heading font-black text-lg">Recent earning activity</h2>
        {w.recent_activity.length === 0 ? <div className="text-sm text-white/40">Launch an app from <Link to="/browse" className="text-[#00F0FF]">Browse</Link> to start earning.</div> :
          w.recent_activity.map((a, i) => <div key={i} className="flex justify-between text-xs glass rounded-lg px-3 py-2"><span>{a.label}</span><span className="text-white/40">{new Date(a.at).toLocaleString()}</span></div>)}
      </section>
      <p className="text-[11px] text-white/40 flex gap-2"><ShieldCheck className="w-4 h-4 text-[#39FF14] shrink-0" /> Balances are calculated from a tamper-proof ledger on our servers. Duplicate requests are blocked automatically.</p>
    </div>
  );
}
